import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONTACT, waLink } from "@/data";
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[var(--noir)]/80 backdrop-blur-xl border-b border-[rgba(200,149,54,0.25)] py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <nav className="section-pad flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 leading-none">
          <img
            src={CONTACT.logo}
            alt="Brahmsara logo"
            className={`h-11 w-11 md:h-12 md:w-12 rounded-full object-cover ring-1 transition-all duration-500 ${scrolled ? "ring-[rgba(200,149,54,0.5)]" : "ring-[rgba(233,172,70,0.6)] shadow-[0_0_24px_rgba(233,172,70,0.35)]"}`}
          />
          <span className="flex flex-col">
            <span className={`font-serif-display text-2xl md:text-3xl tracking-tight transition-colors duration-500 ${scrolled ? "text-[var(--ink)]" : "text-[var(--cream)]"}`}>
              Brahmsara
            </span>
            <span className="overline text-[0.5rem] mt-0.5">{CONTACT.tagline}</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}-link`}
              className={`font-sans-ui text-[0.8rem] tracking-wide relative group transition-colors duration-300 ${scrolled ? "text-[var(--muted-ink)] hover:text-[var(--ink)]" : "text-[var(--cream)]/80 hover:text-[var(--gold-bright)]"}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--gold)] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </div>

        <div className="hidden lg:block">
          <a
            href={waLink("Namaste! I'd like to book a session with Brahmsara.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-btn"
            className={`font-sans-ui text-[0.78rem] tracking-wide px-6 py-3 rounded-full transition-colors duration-400 ${scrolled ? "bg-[var(--gold)] text-[var(--noir)] hover:bg-[var(--gold)]" : "bg-[var(--gold)] text-[var(--noir)] hover:bg-[var(--gold-bright)]"}`}
          >
            Book a Session
          </a>
        </div>

        <button
          className={`lg:hidden transition-colors duration-500 ${scrolled ? "text-[var(--ink)]" : "text-[var(--cream)]"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-[var(--noir)]/95 backdrop-blur-xl border-t border-[rgba(200,149,54,0.2)]"
          >
            <div className="section-pad py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className="font-sans-ui text-sm tracking-wide text-[var(--muted-ink)] hover:text-[var(--ink)]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink("Namaste! I'd like to book a session with Brahmsara.")}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-book-btn"
                className="mt-2 text-center font-sans-ui text-sm px-6 py-3 bg-[var(--gold)] text-[var(--noir)] rounded-full"
              >
                Book a Session
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
